// Header.js - Top Header Bar

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import useStore, { temporalStore } from '../../store/useStore';
import api from '../../utils/api';
import THEME from '../../styles/theme';
import { generateCompletePDFReport } from '../../utils/pdfReportGenerator';
import { useConfirmDialogStore } from '../../store/useConfirmDialogStore';
import { tutorialCompletedKey, tutorialPausedStepKey } from '../../store/slices/tutorialSlice';
import FeedbackModal from '../Feedback/FeedbackModal';
import FeedbackManager from '../Admin/FeedbackManager';
import UsageManager from '../Admin/UsageManager';

const Header = () => {
  const {
    currentProject,
    projects,
    setProjects,
    setCurrentProject,
    nodes,
    edges,
    calculationResults,
    frequencyMode,
    frequencySingle,
    frequencyStart,
    frequencyStop,
    frequencyStep,
    hasUnsavedChanges,
    markAsSaved,
    setNodes,
    setEdges,
    user,
    showTutorial,
    tutorialStep
  } = useStore();

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');

  // Feedback state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackManagerOpen, setFeedbackManagerOpen] = useState(false);
  const [hasFeedbackUpdates, setHasFeedbackUpdates] = useState(false);
  const [hasNewFeedback, setHasNewFeedback] = useState(false);
  const [usageManagerOpen, setUsageManagerOpen] = useState(false);

  // Check if user is admin
  const isAdmin = user?.isAdmin === true;

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Listen for keyboard shortcut events from MainLayout
  useEffect(() => {
    const handleSaveEvent = () => handleSaveProject();
    const handleSaveAsEvent = () => handleSaveAs();
    const handleNewProjectEvent = () => handleNewProject();
    const handleLoadProjectEvent = () => setIsLoadModalOpen(true);
    const handleCreatePdfEvent = () => handleGenerateReport();

    window.addEventListener('save-project', handleSaveEvent);
    window.addEventListener('save-project-as', handleSaveAsEvent);
    window.addEventListener('new-project', handleNewProjectEvent);
    window.addEventListener('load-project', handleLoadProjectEvent);
    window.addEventListener('create-pdf', handleCreatePdfEvent);

    return () => {
      window.removeEventListener('save-project', handleSaveEvent);
      window.removeEventListener('save-project-as', handleSaveAsEvent);
      window.removeEventListener('new-project', handleNewProjectEvent);
      window.removeEventListener('load-project', handleLoadProjectEvent);
      window.removeEventListener('create-pdf', handleCreatePdfEvent);
    };
  }, [nodes, edges, currentProject, frequencyMode, frequencySingle]);

  // Poll for feedback updates (every 60 seconds)
  useEffect(() => {
    const checkFeedbackUpdates = async () => {
      try {
        // Check for user's feedback updates
        const userUpdates = await api.checkFeedbackUpdates();
        setHasFeedbackUpdates(userUpdates.hasUpdates);

        // If admin, also check for new submissions
        if (isAdmin) {
          const adminUpdates = await api.checkNewFeedback();
          setHasNewFeedback(adminUpdates.hasNew);
        }
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.error('Failed to check feedback updates:', error);
      }
    };

    // Initial check
    checkFeedbackUpdates();

    // Set up polling interval
    const interval = setInterval(checkFeedbackUpdates, 60000);

    return () => clearInterval(interval);
  }, [isAdmin]);


  const loadProjects = async () => {
    try {
      const projectsList = await api.getProjects();
      setProjects(projectsList);

      // Read from the store rather than the render closure: callers such as
      // handleSaveProject set currentProject immediately before calling this.
      const { currentProject: activeProject } = useStore.getState();
      if (!activeProject) {
        if (projectsList.length > 0) {
          // Load first project if available
          handleLoadProject(projectsList[0].id);
        } else {
          // Brand-new account with no projects: start on an unsaved blank
          // project so Save/Save As behave like a first save rather than
          // treating the canvas as having no project at all.
          await initializeBlankProject();
          useStore.getState().markAsSaved();
        }
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const handleNewProject = async () => {
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      const confirmed = await useConfirmDialogStore.getState().showConfirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes.\nCreating a new project will discard them.\n\nContinue?',
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel',
        variant: 'warning'
      });
      if (!confirmed) return;
    }

    await initializeBlankProject();
  };

  // Create an in-memory (id === null) project with the default Signal Generator
  // and Measurement nodes. Shared by New Project and the empty-account startup path.
  const initializeBlankProject = async () => {
    // Show loading overlay before making any changes
    const { ENABLE_CANVAS_LOAD_TRANSITION, setCanvasLoading } = useStore.getState();
    if (ENABLE_CANVAS_LOAD_TRANSITION) {
      setCanvasLoading(true);
      // Wait for opacity to take effect before changing canvas content
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    // Create temporary in-memory project (not saved to DB yet)
    const tempProject = {
      id: null,  // null indicates unsaved project
      name: 'Untitled Project',
      description: '',
      frequency_mode: 'single',
      frequency_single: 1700
    };

    setCurrentProject(tempProject);

    // Create default Signal Generator node
    const defaultInputNode = {
      id: 'node-default-input',
      type: 'inputSource',
      position: { x: 400, y: 300 },
      dragHandle: '.drag-handle',
      data: {
        name: 'Signal Generator',
        parameters: {
          frequency: { name: 'Frequency', value: 1700, unit: 'MHz', min: 0.1, max: 100000, tracked: true, displayOnCard: true, description: 'Operating frequency' },
          power: { name: 'Power', value: -10, unit: 'dBm', min: -150, max: 50, tracked: true, displayOnCard: true, description: 'Output power' },
          temperature: { name: 'Temperature', value: 25, unit: '°C', min: -273, max: 200, tracked: true, displayOnCard: false, description: 'Operating temperature' },
          impedance: { name: 'Impedance', value: 50, unit: 'Ω', min: 1, max: 1000, tracked: true, displayOnCard: false, description: 'Source impedance' }
        }
      }
    };

    // Create default Measurement node positioned to the right
    const defaultMeasurementNode = {
      id: 'node-default-measurement',
      type: 'measurement',
      position: { x: 650, y: 300 },
      dragHandle: '.drag-handle',
      data: {
        name: 'Measurement',
        parameters: {
          gain: { name: 'Gain', value: 0, unit: 'dB', min: -50, max: 50, tracked: true, displayOnCard: false, description: 'Passthrough gain (typically 0 dB)' },
          noiseFigure: { name: 'NF', value: 0, unit: 'dB', min: 0, max: 50, tracked: true, displayOnCard: false, description: 'Passthrough noise figure (typically 0 dB)' }
        }
      }
    };

    // Create edge connecting Input Source to Measurement
    const defaultEdge = {
      id: 'edge-default',
      source: 'node-default-input',
      sourceHandle: 'rfOut',
      target: 'node-default-measurement',
      targetHandle: 'rfIn',
      type: 'default',
      animated: false,
      style: { stroke: '#ff6b6b', strokeWidth: 2 }
    };

    // Set nodes and edges (this will mark as modified)
    setNodes([defaultInputNode, defaultMeasurementNode]);
    setEdges([defaultEdge]);

    // Reset frequency mode to single for new projects
    useStore.getState().setFrequencyMode('single');
    useStore.getState().setFrequencySingle(1700);

    // Clear last saved timestamp (project hasn't been saved yet)
    setLastSaved(null);

    // Clear history for new project (using Zundo temporal store)
    temporalStore.getState().clear();

    // Trigger fitView after creating new project
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('fit-view-requested'));
      });
    });
  };

  const handleLoadProject = async (projectId) => {
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      const confirmed = await useConfirmDialogStore.getState().showConfirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes.\nLoading another project will discard them.\n\nContinue?',
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel',
        variant: 'warning'
      });
      if (!confirmed) return;
    }

    // Show loading overlay if feature enabled
    const { ENABLE_CANVAS_LOAD_TRANSITION, setCanvasLoading } = useStore.getState();
    if (ENABLE_CANVAS_LOAD_TRANSITION) {
      setCanvasLoading(true);
      // Wait for opacity to take effect before changing canvas content
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    try {
      const project = await api.getProject(projectId);
      setCurrentProject(project);

      // Use setNodesWithoutModifying and setEdgesWithoutModifying for loading
      // so that loading a project doesn't trigger the unsaved changes flag
      useStore.getState().setNodesWithoutModifying(project.nodes || []);
      useStore.getState().setEdgesWithoutModifying(project.edges || []);
      useStore.getState().setFrequencyMode(project.frequency_mode || 'single');
      useStore.getState().setFrequencySingle(project.frequency_single || 1700);

      // Load frequency data for catalog components that have it
      const catalogNodes = (project.nodes || []).filter(
        n => n.data?.isCatalog && n.data?.hasFreqData && !n.data?.parameters?.freqDbData?.value
      );
      if (catalogNodes.length > 0) {
        const { loadFrequencyData, setNodesWithoutModifying } = useStore.getState();
        const updatedNodes = [...(project.nodes || [])];

        for (const node of catalogNodes) {
          try {
            const voltage = node.data.parameters?.selectedVoltage?.value ||
                           node.data.parameters?.supplyVoltage?.value || 5.0;
            const freqData = await loadFrequencyData(node.data.partNumber, voltage);
            if (freqData && freqData.length > 0 && node.data.parameters?.freqDbData) {
              // Find and update the node in our array
              const nodeIndex = updatedNodes.findIndex(n => n.id === node.id);
              if (nodeIndex !== -1) {
                updatedNodes[nodeIndex] = {
                  ...updatedNodes[nodeIndex],
                  data: {
                    ...updatedNodes[nodeIndex].data,
                    parameters: {
                      ...updatedNodes[nodeIndex].data.parameters,
                      freqDbData: {
                        ...updatedNodes[nodeIndex].data.parameters.freqDbData,
                        value: freqData
                      }
                    }
                  }
                };
              }
            }
          } catch (error) {
            console.error(`Failed to load frequency data for ${node.data.partNumber}:`, error);
          }
        }

        // Update all nodes at once without triggering unsaved changes
        setNodesWithoutModifying(updatedNodes);
      }

      // Set last saved time from project's updated_at timestamp
      if (project.updated_at) {
        setLastSaved(new Date(project.updated_at));
      }

      // Mark as saved (no unsaved changes after load)
      markAsSaved();

      // Trigger fitView after loading to show all nodes
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent('fit-view-requested'));
        });
      });
    } catch (error) {
      // Clear loading overlay on error
      const { ENABLE_CANVAS_LOAD_TRANSITION, setCanvasLoading } = useStore.getState();
      if (ENABLE_CANVAS_LOAD_TRANSITION) {
        setCanvasLoading(false);
      }
      console.error('Failed to load project:', error);
    }
  };

  const handleSaveProject = async () => {
    if (!currentProject) {
      handleNewProject();
      return;
    }

    setIsSaving(true);
    setSaveMessage('Saving...');

    try {
      // Check if this is a new unsaved project (id === null)
      if (currentProject.id === null) {
        // First save - create new project in database
        const newProject = await api.createProject({
          name: currentProject.name,
          description: currentProject.description || '',
          frequency_mode: frequencyMode,
          frequency_single: frequencySingle,
          nodes,
          edges
        });

        // Update currentProject with the new DB id
        setCurrentProject(newProject);
        await loadProjects();

        setLastSaved(new Date());
        setSaveMessage('Saved!');
        setTimeout(() => setSaveMessage(''), 2000);
      } else {
        // Existing project - update it
        await api.updateProject(currentProject.id, {
          ...currentProject,
          nodes,
          edges,
          frequency_mode: frequencyMode,
          frequency_single: frequencySingle
        });

        setLastSaved(new Date());
        setSaveMessage('Saved!');
        setTimeout(() => setSaveMessage(''), 2000);
      }

      // Mark as saved after successful save
      markAsSaved();
    } catch (error) {
      console.error('Failed to save project:', error);
      if (error.message.includes('session has expired')) {
        await useConfirmDialogStore.getState().showAlert({
          title: 'Session Expired',
          message: error.message,
          confirmLabel: 'OK',
          variant: 'warning'
        });
      } else {
        setSaveMessage(error.message);
        setTimeout(() => setSaveMessage(''), 5000);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAs = async () => {
    const newName = prompt('Enter new project name:', currentProject?.name ? `${currentProject.name} (Copy)` : 'New Project');
    if (!newName) return;

    try {
      const newProject = await api.createProject({
        name: newName,
        description: currentProject?.description || '',
        frequency_mode: frequencyMode,
        frequency_single: frequencySingle,
        nodes,
        edges
      });

      setCurrentProject(newProject);
      await loadProjects();
      setLastSaved(new Date());
      setSaveMessage('Saved as new project!');
      setTimeout(() => setSaveMessage(''), 2000);

      // Mark as saved after save as
      markAsSaved();
    } catch (error) {
      console.error('Failed to save as:', error);
      setSaveMessage('Save As failed');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleRenameProject = async () => {
    if (!currentProject || !editedName.trim()) return;

    // If project hasn't been saved yet (id === null), just update the name locally
    if (currentProject.id === null) {
      setCurrentProject({ ...currentProject, name: editedName.trim() });
      setIsEditingName(false);
      return;
    }

    try {
      await api.updateProject(currentProject.id, {
        ...currentProject,
        name: editedName.trim()
      });

      setCurrentProject({ ...currentProject, name: editedName.trim() });
      await loadProjects();
      setIsEditingName(false);
      setSaveMessage('Renamed!');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Failed to rename project:', error);
      setSaveMessage('Rename failed');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const startEditingName = () => {
    if (currentProject) {
      setEditedName(currentProject.name);
      setIsEditingName(true);
    }
  };

  const formatLastSaved = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;

    return date.toLocaleDateString();
  };

  const handleLogout = () => {
    api.logout();
    useStore.getState().logout();
    window.location.reload();
  };

  const handleExportPDF = async () => {
    const { exportToPDF } = await import('../../utils/exportUtils');
    const { calculationResults } = useStore.getState();
    exportToPDF(currentProject?.name || 'GChain Project', nodes, calculationResults);
  };

  const handleExportExcel = async () => {
    const { exportToExcel } = await import('../../utils/exportUtils');
    const { calculationResults } = useStore.getState();
    exportToExcel(currentProject?.name || 'GChain Project', nodes, calculationResults);
  };

  const handleExportJSON = async () => {
    const { exportToJSON } = await import('../../utils/exportUtils');
    const { calculationResults } = useStore.getState();
    exportToJSON({
      name: currentProject?.name || 'GChain Project',
      nodes,
      edges,
      calculationResults
    });
  };

  const handleImportJSON = async () => {
    const { importFromJSON } = await import('../../utils/exportUtils');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.gchain.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const projectData = await importFromJSON(file);
          if (projectData) {
            setNodes(projectData.nodes || []);
            setEdges(projectData.edges || []);
            setSaveMessage('Project imported successfully');
            setTimeout(() => setSaveMessage(''), 2000);
          }
        } catch (error) {
          console.error('Import failed:', error);
          setSaveMessage('Import failed: ' + error.message);
          setTimeout(() => setSaveMessage(''), 3000);
        }
      }
    };
    input.click();
  };

  const handleLoadFeedbackProject = async (projectData) => {
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      const confirmed = await useConfirmDialogStore.getState().showConfirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes.\nLoading the feedback project will discard them.\n\nContinue?',
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel'
      });

      if (!confirmed) return;
    }

    // Show loading overlay before making any changes
    const { ENABLE_CANVAS_LOAD_TRANSITION, setCanvasLoading } = useStore.getState();
    if (ENABLE_CANVAS_LOAD_TRANSITION) {
      setCanvasLoading(true);
      // Wait for opacity to take effect before changing canvas content
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    // Load the project data into the canvas
    setNodes(projectData.nodes || []);
    setEdges(projectData.edges || []);

    // Close the feedback manager
    setFeedbackManagerOpen(false);
    setHasNewFeedback(false);

    // Trigger fitView after loading to show all nodes (same as handleLoadProject)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('fit-view-requested'));
      });
    });

    setSaveMessage('Feedback project loaded');
    setTimeout(() => setSaveMessage(''), 2000);
  };

  const handleRestartTutorial = () => {
    localStorage.removeItem(tutorialCompletedKey(user.id));
    localStorage.removeItem(tutorialPausedStepKey(user.id));
    useStore.getState().setShowWelcomeModal(true);
  };

  const handleResumeTutorial = () => {
    useStore.getState().resumeTutorial();
  };

  // Check if there's a paused tutorial
  const canResume = useStore.getState().canResumeTutorial();
  const pausedStep = useStore.getState().getPausedStep();

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    setSaveMessage('Generating report...');

    try {
      // Get the React Flow canvas element
      const canvasElement = document.querySelector('.react-flow__viewport');

      if (!canvasElement) {
        throw new Error('Canvas not found');
      }

      // Get React Flow instance from the window (it's exposed by MainLayout)
      // Alternative: pass it as a prop from MainLayout
      const reactFlowInstance = window.reactFlowInstance || null;

      // Prepare project data
      const projectData = {
        projectName: currentProject?.name || 'Untitled Project',
        projectDescription: currentProject?.description || '',
        createdAt: currentProject?.created_at,
        updatedAt: currentProject?.updated_at || new Date(),
        nodes,
        edges,
        calculationResults,
        frequencyMode,
        frequencySingle,
        frequencyStart,
        frequencyStop,
        frequencyStep
      };

      // Generate the PDF report with React Flow instance for proper viewport handling
      const result = await generateCompletePDFReport(projectData, canvasElement, reactFlowInstance);

      if (result.success) {
        setSaveMessage(`Report generated: ${result.fileName}`);
        setTimeout(() => setSaveMessage(''), 3000);
        api.track('event', 'pdf_export');
        // Track for tutorial - pdfExported action can happen on any step
        const { showTutorial, completeTutorialAction } = useStore.getState();
        if (showTutorial) {
          completeTutorialAction('pdfExported');
        }
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      setSaveMessage('Report generation failed');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Boost header z-index during tutorial step 25 to allow File dropdown to work
  const headerZIndex = showTutorial && tutorialStep === 25 ? 100000 : undefined;

  return (
    <Container style={headerZIndex ? { position: 'relative', zIndex: headerZIndex } : undefined}>
      <Left>
        <AppTitle>GChain</AppTitle>

        {/* File Menu */}
        <DropdownContainer className="file-dropdown" $tutorialActive={showTutorial && tutorialStep === 25}>
          <Button>File ▼</Button>
          <DropdownMenu $align="left" $tutorialActive={showTutorial && tutorialStep === 25}>
            <DropdownItem onClick={handleNewProject}>
              <span>New Project</span>
              <Shortcut>Ctrl+Shift+N</Shortcut>
            </DropdownItem>
            <DropdownItem onClick={() => setIsLoadModalOpen(true)}>
              <span>Load Project</span>
              <Shortcut>Ctrl+L</Shortcut>
            </DropdownItem>
            <DropdownDivider />
            <DropdownItem onClick={handleSaveProject} disabled={isSaving}>
              <span>{isSaving ? 'Saving...' : 'Save Project'}</span>
              <Shortcut>Ctrl+S</Shortcut>
            </DropdownItem>
            <DropdownItem onClick={handleSaveAs}>
              <span>Save As...</span>
              <Shortcut>Ctrl+Shift+S</Shortcut>
            </DropdownItem>
            <DropdownDivider />
            <DropdownItem onClick={handleExportJSON}>Export JSON</DropdownItem>
            <DropdownItem onClick={handleImportJSON}>Import JSON</DropdownItem>
            <DropdownDivider />
            <DropdownItem onClick={handleGenerateReport} disabled={isGeneratingReport}>
              <span>{isGeneratingReport ? 'Creating PDF...' : 'Create PDF'}</span>
              <Shortcut>Ctrl+P</Shortcut>
            </DropdownItem>
          </DropdownMenu>
        </DropdownContainer>

      </Left>

      <Center>
        {isEditingName ? (
          <ProjectNameInput
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleRenameProject}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameProject();
              if (e.key === 'Escape') setIsEditingName(false);
            }}
            autoFocus
          />
        ) : (
          <ProjectNameContainer>
            <ProjectLabel>Loaded Project:</ProjectLabel>
            <ProjectNameDisplay
              onDoubleClick={startEditingName}
              title="Double-click to rename"
            >
              {currentProject?.name || 'Untitled Project'}
            </ProjectNameDisplay>
            {lastSaved && (
              <LastSavedText>Saved {formatLastSaved(lastSaved)}</LastSavedText>
            )}
          </ProjectNameContainer>
        )}
      </Center>

      {/* Load Project Modal */}
      {isLoadModalOpen && (
        <ModalOverlay onClick={() => setIsLoadModalOpen(false)}>
          <LoadModal onClick={(e) => e.stopPropagation()}>
            <LoadModalHeader>
              <LoadModalTitle>Load Project</LoadModalTitle>
              <CloseButton onClick={() => setIsLoadModalOpen(false)}>×</CloseButton>
            </LoadModalHeader>
            <SearchInput
              type="text"
              placeholder="Search projects..."
              value={projectSearchTerm}
              onChange={(e) => setProjectSearchTerm(e.target.value)}
              autoFocus
            />
            <ProjectList>
              {projects
                .filter(p => p.name.toLowerCase().includes(projectSearchTerm.toLowerCase()))
                .map(project => (
                  <ProjectListItem
                    key={project.id}
                    $isActive={project.id === currentProject?.id}
                    onClick={() => {
                      handleLoadProject(project.id);
                      setIsLoadModalOpen(false);
                      setProjectSearchTerm('');
                    }}
                  >
                    <ProjectItemName>{project.name}</ProjectItemName>
                    {project.id === currentProject?.id && <CurrentBadge>Current</CurrentBadge>}
                  </ProjectListItem>
                ))}
              {projects.filter(p => p.name.toLowerCase().includes(projectSearchTerm.toLowerCase())).length === 0 && (
                <EmptyState>No projects found</EmptyState>
              )}
            </ProjectList>
          </LoadModal>
        </ModalOverlay>
      )}

      <Right>
        {saveMessage && <SaveMessage>{saveMessage}</SaveMessage>}

        {/* Help Menu */}
        <DropdownContainer>
          <Button id="help-menu-button">
            Help {(hasFeedbackUpdates || (isAdmin && hasNewFeedback)) && <NotificationDot />} ▼
          </Button>
          <DropdownMenu $align="right">
            <DropdownItem onClick={() => {
              setFeedbackModalOpen(true);
              // Track for tutorial - feedbackOpened action can happen on any step
              const { showTutorial, completeTutorialAction } = useStore.getState();
              if (showTutorial) {
                completeTutorialAction('feedbackOpened');
              }
            }}>
              Updates/Feedback {hasFeedbackUpdates && <DropdownBadge />}
            </DropdownItem>
            <DropdownDivider />
            {canResume && (
              <DropdownItem onClick={handleResumeTutorial}>
                Resume Tutorial (Step {pausedStep + 1} of 27)
              </DropdownItem>
            )}
            <DropdownItem onClick={handleRestartTutorial}>Restart Tutorial</DropdownItem>
            {isAdmin && (
              <>
                <DropdownDivider />
                <DropdownItem onClick={() => setFeedbackManagerOpen(true)}>
                  Feedback Manager {hasNewFeedback && <DropdownBadge />}
                </DropdownItem>
                <DropdownItem onClick={() => setUsageManagerOpen(true)}>
                  Usage &amp; Activity
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </DropdownContainer>

        <Button onClick={handleLogout}>Logout</Button>
      </Right>

      {/* Feedback Modals */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => {
          setFeedbackModalOpen(false);
          setHasFeedbackUpdates(false);
        }}
      />

      {isAdmin && (
        <>
          <FeedbackManager
            isOpen={feedbackManagerOpen}
            onClose={() => {
              setFeedbackManagerOpen(false);
              setHasNewFeedback(false);
            }}
            onLoadProject={handleLoadFeedbackProject}
          />
          <UsageManager
            isOpen={usageManagerOpen}
            onClose={() => setUsageManagerOpen(false)}
          />
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  background: ${THEME.sidebarBackground};
  border-bottom: 1px solid #404040;
  padding: 0 ${THEME.spacing.lg};
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.md};
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0 40px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.md};
`;

const Logo = styled.h1`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xl};
  font-weight: bold;
  margin-right: ${THEME.spacing.lg};
`;

const Button = styled.button`
  background: ${THEME.buttonSecondary};
  color: ${THEME.textPrimary};
  padding: ${THEME.spacing.sm} ${THEME.spacing.lg};
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.md};
  transition: background ${THEME.transitionFast};

  &:hover:not(:disabled) {
    background: #505050;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const AppTitle = styled.div`
  color: ${THEME.textPrimary};
  font-weight: 700;
  font-size: 24px;
  text-align: left;
`;

const ProjectNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProjectLabel = styled.span`
  color: ${THEME.textMuted};
  font-size: 13px;
  font-weight: normal;
`;

const ProjectNameDisplay = styled.span`
  color: ${THEME.textPrimary};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: ${THEME.borderRadiusSmall};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 100px;
  z-index: 2000;
`;

const LoadModal = styled.div`
  background: ${THEME.panelBackground};
  border: 1px solid #404040;
  border-radius: ${THEME.borderRadius};
  width: 500px;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const LoadModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #404040;
`;

const LoadModalTitle = styled.h2`
  color: ${THEME.textPrimary};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${THEME.textMuted};
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: ${THEME.textPrimary};
  }
`;

const SearchInput = styled.input`
  background: ${THEME.inputBackground};
  border: none;
  border-bottom: 1px solid #404040;
  color: ${THEME.textPrimary};
  font-size: 16px;
  padding: 14px 20px;
  outline: none;

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const ProjectList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const ProjectListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  cursor: pointer;
  color: ${THEME.textPrimary};
  background: ${props => props.$isActive ? 'rgba(255, 255, 255, 0.08)' : 'transparent'};

  &:hover {
    background: ${THEME.buttonSecondary};
  }
`;

const ProjectItemName = styled.span`
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CurrentBadge = styled.span`
  font-size: 11px;
  color: ${THEME.textMuted};
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
`;

const EmptyState = styled.div`
  padding: 24px 20px;
  text-align: center;
  color: ${THEME.textMuted};
  font-size: 14px;
`;

const ProjectNameInput = styled.input`
  background: ${THEME.inputBackground};
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xl};
  font-weight: 600;
  border: 2px solid ${THEME.accentPrimary};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  width: 100%;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${THEME.accentPrimary};
  }
`;

const LastSavedText = styled.span`
  color: ${THEME.textMuted};
  font-size: 11px;
  font-weight: normal;
`;

const SaveMessage = styled.span`
  color: ${THEME.success};
  font-size: ${THEME.fontSize.sm};
`;

const DropdownContainer = styled.div`
  position: relative;
  z-index: ${props => props.$tutorialActive ? 100000 : 'auto'};

  &:hover > div {
    display: block;
  }
`;

const DropdownMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  ${props => props.$align === 'left' ? 'left: 0;' : 'right: 0;'}
  background: ${THEME.panelBackground};
  padding-top: 4px;

  /* Invisible bridge to prevent menu from closing when moving mouse from button to menu */
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 0;
    right: 0;
    height: 12px;
  }
  border: 1px solid #404040;
  border-radius: ${THEME.borderRadiusSmall};
  margin-top: 4px;
  width: max-content;
  box-shadow: ${THEME.shadowMedium};
  z-index: ${props => props.$tutorialActive ? 100001 : 1000};
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: #404040;
  margin: 4px 0;
`;

const DropdownItem = styled.div`
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${props => props.disabled ? THEME.textMuted : THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: background ${THEME.transitionFast};
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.sm};
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  &:hover {
    background: ${props => props.disabled ? 'transparent' : THEME.buttonSecondary};
  }
`;

const NotificationDot = styled.span`
  width: 8px;
  height: 8px;
  background: ${THEME.accentPrimary};
  border-radius: 50%;
  margin-left: 4px;
`;

const DropdownBadge = styled.span`
  width: 6px;
  height: 6px;
  background: ${THEME.accentPrimary};
  border-radius: 50%;
  margin-left: auto;
`;

const Shortcut = styled.span`
  font-size: 11px;
  color: ${THEME.textMuted};
  margin-left: auto;
  padding-left: 24px;
`;

export default Header;
